import { UserId } from '@cibo/core'
import { AssignUserControl, AuthUserPermission, Can } from '@cibo/profile'
import { FriendlyError, Markdown, ResponsiveDialog } from '@cibo/ui'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  Stack,
  TextField,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAssignFields } from '../../../queries'

interface AssignUserEditorProps {
  open: boolean
  onCancel: () => void
  onSuccess: () => void
  resourceIds: string[]
}

export const AssignUserEditor = ({
  open,
  onCancel,
  onSuccess,
  resourceIds,
}: AssignUserEditorProps) => {
  const { t } = useTranslation('@cibo/landmanager/Editors/AssignUserEditor')
  const assignFields = useAssignFields()
  const [customMessage, setCustomMessage] = useState<string>()
  const [messageUser, setMessageUser] = useState(true)
  const [userId, setUserId] = useState<UserId>()
  const [fullText, setFullText] = useState<string>()
  const { enqueueSnackbar } = useSnackbar()
  const count = resourceIds.length

  useEffect(() => {
    assignFields.reset()
    setUserId(undefined)
    setFullText(undefined)
    setCustomMessage(undefined)
  }, [open])

  const handleAssign = () => {
    !!userId &&
      assignFields
        .mutateAsync({
          messageUser,
          customMessage,
          userId,
          resourceIds,
        })
        .then(e => {
          enqueueSnackbar(
            t('successMessage', { context: messageUser ? 'withMessage' : 'withoutMessage', count }),
            { variant: 'success' }
          )
          onSuccess()
        })
  }

  return (
    <ResponsiveDialog maxWidth={'sm'} fullWidth open={open} onClose={onCancel}>
      <DialogTitle>{t('assignFieldsTitle', { count })}</DialogTitle>
      <DialogContent>
        <Markdown>{t('assignFieldsBody', { count })}</Markdown>
        <Stack spacing={3}>
          <AssignUserControl
            setFullText={setFullText}
            fullText={fullText}
            userId={userId}
            setUserId={setUserId}
          />
          {/* @todo re-enable when https://cibotech.atlassian.net/browse/CPD-2245 is complete */}
          {/* <Can useAny={AuthUserPermission.FIELD_ASSIGN_OWNER_MESSAGE}> */}
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={messageUser}
                  onChange={(e, checked) => setMessageUser(checked)}
                />
              }
              label={t('sendMessage')}
            />
            <FormHelperText>{t('sendMessageHelper', { count })}</FormHelperText>
          </FormGroup>
          <Can useAny={AuthUserPermission.FIELD_ASSIGN_OWNER_MESSAGE_CUSTOM}>
            <FormGroup>
              <InputLabel></InputLabel>
              <TextField
                multiline
                rows={6}
                placeholder={t('optionalMessage')}
                value={customMessage}
                onChange={({ target: { value } }) => {
                  setMessageUser(true)
                  setCustomMessage(value)
                }}
                disabled={assignFields.isLoading}
              />
            </FormGroup>
          </Can>
          {/* </Can> */}
        </Stack>
        {assignFields.isError && <FriendlyError message={t('errorMessage', { count })} />}
      </DialogContent>
      <DialogActions>
        <Button disabled={assignFields.isLoading} onClick={onCancel}>
          {t('cancel')}
        </Button>
        <LoadingButton
          loading={assignFields.isLoading}
          disabled={!userId}
          variant="contained"
          onClick={handleAssign}
        >
          {t('assign')}
        </LoadingButton>
      </DialogActions>
    </ResponsiveDialog>
  )
}
